import {objectFromEntries} from '@benchling/js-utils';
import {noop} from 'jquery';
import React, {useMemo} from 'react';

import {assert} from 'Asserts';
import SimpleTableEditable, {standaloneProps} from 'notebook/simple-tables/SimpleTableEditable';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {Table} from 'notebook/tables/constants/TableConstants';
import {TableDimension} from 'notebook/tables/constants/TableSelectionConstants';
import ErrorTriangle, {TriangleLocation, TriangleState} from 'notebook/tables/errors/ErrorTriangle';
import {CLINICAL_STUDIES_VALIDATION_CONSTANTS} from 'studies/StudyConstants';
import {getItemCounts, useStudyDesignItemTableSharedProps} from 'studies/typed/utils/StudyDesignItemUtil';
import {COLORS} from 'ui-library/ColorPickerConstants';
import {Optional} from 'util/Types';

export interface TreatmentGroup {
  id?: string;
  name: string;
  label: string;
  description: string | null;
  targetNumberOfSubjects: number;
  validationState?: TreatmentGroupValidationState;
  isReadyForExecution: boolean;
}

export type PersistedTreatmentGroup = TreatmentGroup & Required<Pick<TreatmentGroup, 'id'>>;

/**
 * Any truthy value in the validation state is a blocking validation error for saving the study design.
 */
export interface TreatmentGroupValidationState {
  isMissingName: boolean;
  isDuplicateName: boolean;
  isNameLengthOverLimit: boolean;
  isMissingLabel: boolean;
  isDuplicateLabel: boolean;
  isLabelLengthOverLimit: boolean;
  isDescLengthOverLimit: boolean;
  isTargetNumberOfSubjectsUnderMin: boolean;
  isTargetNumberOfSubjectsNotAnInt: boolean;
}

export const INACTIVE_TREATMENT_GROUP_TOOLTIP = 'Proceed to execution to finalize this treatment group';

/**
 *  Update data when any individual cell is updated.
 */
export function updateTableCells(
  table: Table,
  setTreatmentGroups: (treatmentGroups: Array<TreatmentGroup>) => void,
  persistedTreatmentGroupsById: Record<string, TreatmentGroup>
): void {
  assert(table.rowData != null, 'Table must have rowData');
  const treatmentGroupIdsByRow = table.rowData.map((rowData) =>
    rowData.rowMetadata ? rowData.rowMetadata.rowId : undefined
  );
  const newTreatmentGroups = table.table.map((treatmentGroup, index) => {
    const id = treatmentGroupIdsByRow[index];
    return {
      id,
      name: treatmentGroup[0].text.trimEnd(),
      label: treatmentGroup[1].text.trimEnd(),
      description: treatmentGroup[2].text.trimEnd(),
      targetNumberOfSubjects: Number(treatmentGroup[3].text),
      isReadyForExecution: id ? persistedTreatmentGroupsById[id].isReadyForExecution : false,
    };
  });
  const nameCounts = getItemCounts(
    newTreatmentGroups.map((treatmentGroup) => treatmentGroup.name.toLowerCase())
  );
  const labelCounts = getItemCounts(
    newTreatmentGroups.map((treatmentGroup) => treatmentGroup.label.toLowerCase())
  );
  setTreatmentGroups(
    newTreatmentGroups.map((treatmentGroup) => ({
      ...treatmentGroup,
      validationState: {
        isMissingName: treatmentGroup.name.length === 0,
        isDuplicateName: nameCounts.get(treatmentGroup.name.toLowerCase())! > 1,
        isNameLengthOverLimit:
          treatmentGroup.name.length > CLINICAL_STUDIES_VALIDATION_CONSTANTS.TREATMENT_GROUP_NAME_MAX_LENGTH,
        isMissingLabel: treatmentGroup.label.length === 0,
        isDuplicateLabel: labelCounts.get(treatmentGroup.label.toLowerCase())! > 1,
        isLabelLengthOverLimit:
          treatmentGroup.label.length >
          CLINICAL_STUDIES_VALIDATION_CONSTANTS.TREATMENT_GROUP_LABEL_MAX_LENGTH,
        isDescLengthOverLimit:
          treatmentGroup.description.length >
          CLINICAL_STUDIES_VALIDATION_CONSTANTS.TREATMENT_GROUP_DESCRIPTION_MAX_LENGTH,
        isTargetNumberOfSubjectsUnderMin:
          treatmentGroup.targetNumberOfSubjects <
          CLINICAL_STUDIES_VALIDATION_CONSTANTS.MIN_TARGET_NUMBER_OF_SUBJECTS,
        isTargetNumberOfSubjectsNotAnInt: !Number.isInteger(treatmentGroup.targetNumberOfSubjects),
      },
    }))
  );
}

const makeErrorTriangle = (text: string): JSX.Element => (
  <ErrorTriangle
    dataTestAttrs={{component: 'TreatmentGroupTable', element: 'validationError'}}
    state={TriangleState.ILLEGAL}
    error={{type: '', message: text}}
    location={TriangleLocation.CELL}
  />
);

function makeInitialTreatmentGroupTable(
  tableTitle: string,
  treatmentGroups: Array<TreatmentGroup>,
  locked: boolean
): Table {
  const validationStatePerTreatmentGroup = treatmentGroups.map(
    (treatmentGroup) =>
      treatmentGroup.validationState ?? {
        isMissingName: false,
        isDuplicateName: false,
        isNameLengthOverLimit: false,
        isMissingLabel: false,
        isDuplicateLabel: false,
        isLabelLengthOverLimit: false,
        isDescLengthOverLimit: false,
        isTargetNumberOfSubjectsUnderMin: false,
        isTargetNumberOfSubjectsNotAnInt: false,
      }
  );
  const getNameErrorTriangle = (index: number): Optional<JSX.Element> => {
    if (validationStatePerTreatmentGroup[index].isMissingName) {
      return makeErrorTriangle('Treatment group name is required');
    } else if (validationStatePerTreatmentGroup[index].isNameLengthOverLimit) {
      return makeErrorTriangle(
        `Treatment group name may not exceed ${CLINICAL_STUDIES_VALIDATION_CONSTANTS.TREATMENT_GROUP_NAME_MAX_LENGTH} characters`
      );
    } else if (validationStatePerTreatmentGroup[index].isDuplicateName) {
      return makeErrorTriangle('Treatment group name already in use');
    } else {
      return undefined;
    }
  };

  const getDescErrorTriangle = (index: number): Optional<JSX.Element> => {
    if (validationStatePerTreatmentGroup[index].isDescLengthOverLimit) {
      return makeErrorTriangle(
        `Treatment group description may not exceed ${
          CLINICAL_STUDIES_VALIDATION_CONSTANTS.TREATMENT_GROUP_DESCRIPTION_MAX_LENGTH
        } characters`
      );
    } else {
      return undefined;
    }
  };

  const getLabelErrorTriangle = (index: number): Optional<JSX.Element> => {
    if (validationStatePerTreatmentGroup[index].isMissingLabel) {
      return makeErrorTriangle('Treatment group label is required');
    } else if (validationStatePerTreatmentGroup[index].isLabelLengthOverLimit) {
      return makeErrorTriangle(
        `Treatment group label may not exceed ${
          CLINICAL_STUDIES_VALIDATION_CONSTANTS.TREATMENT_GROUP_LABEL_MAX_LENGTH
        } characters`
      );
    } else if (validationStatePerTreatmentGroup[index].isDuplicateLabel) {
      return makeErrorTriangle('Treatment group label already in use');
    } else {
      return undefined;
    }
  };

  const getTargetNumberOfSubjectsErrorTriangle = (index: number): Optional<JSX.Element> => {
    if (validationStatePerTreatmentGroup[index].isTargetNumberOfSubjectsUnderMin) {
      return makeErrorTriangle('Target number of subjects must be positive');
    } else if (validationStatePerTreatmentGroup[index].isTargetNumberOfSubjectsNotAnInt) {
      return makeErrorTriangle('Target number of subjects must an integer');
    } else {
      return undefined;
    }
  };

  const makeCell = (
    text: string,
    index: number,
    {locked}: {locked: boolean},
    getErrorTriangle: (index: number) => Optional<JSX.Element>
  ): Cell => ({
    text,
    locked,
    bold: false,
    align: 'left',
    backgroundColor: COLORS.WHITE,
    userAnnotationIndicator: getErrorTriangle(index),
  });

  const columnLabels = ['Name', 'Label', 'Description', 'Target # of subjects'];
  return {
    name: tableTitle,
    widths: columnLabels.map(() => 200),
    columnLabels,
    columnConstraints: columnLabels.map(() => null),
    headerCol: true,
    headerRow: false,
    table: treatmentGroups.map((treatmentGroup, index) => [
      makeCell(treatmentGroup.name, index, {locked: false}, getNameErrorTriangle),
      makeCell(
        treatmentGroup.label,
        index,
        {locked: treatmentGroup.isReadyForExecution},
        getLabelErrorTriangle
      ),
      makeCell(treatmentGroup.description ?? '', index, {locked: false}, getDescErrorTriangle),
      makeCell(
        String(treatmentGroup.targetNumberOfSubjects),
        index,
        {locked: false},
        getTargetNumberOfSubjectsErrorTriangle
      ),
    ]),
    rowData: treatmentGroups.map((treatmentGroup) => ({
      rowMetadata: {rowId: treatmentGroup.id},
      uiData: {
        hasUnsubmittedChanges: !treatmentGroup.isReadyForExecution,
        unsubmittedChangesTooltip: INACTIVE_TREATMENT_GROUP_TOOLTIP,
      },
      isFinalized: treatmentGroup.isReadyForExecution,
    })),
    lockedDims: {allowAddRows: !locked, allowRemoveRows: !locked, allowEditCols: false},
    _hideRename: true,
    allowAddRows: !locked,
    allowRowRemoval: !locked,
    allowColumnEdits: false,
    allowNoRows: false,
    maxRows: CLINICAL_STUDIES_VALIDATION_CONSTANTS.MAX_NUM_TREATMENT_GROUPS_PER_DESIGN,
  };
}

export function TreatmentGroupTable(props: {
  tableTitle: string;
  treatmentGroups: Array<TreatmentGroup>;
  setTreatmentGroups: (treatmentGroups: Array<TreatmentGroup>) => void;
  locked: boolean;
  viewer: boolean;
}): JSX.Element {
  const persistedTreatmentGroupsById = useMemo(
    () =>
      objectFromEntries(
        props.treatmentGroups
          .filter((treatmentGroup): treatmentGroup is PersistedTreatmentGroup => treatmentGroup.id != null)
          .map((treatmentGroup) => [treatmentGroup.id, treatmentGroup])
      ),
    [props.treatmentGroups]
  );
  const sharedProps = useStudyDesignItemTableSharedProps();
  const table = makeInitialTreatmentGroupTable(props.tableTitle, props.treatmentGroups, props.locked);
  return (
    <div className="u-flex-display mediocre">
      <div className="study-design-export--max-width">
        <SimpleTableEditable
          {...standaloneProps}
          {...sharedProps}
          hideFormulaBox={true}
          isCompactBottomSpacing={true}
          isLeftAlignRowHeader={false}
          locked={props.locked}
          viewer={props.viewer}
          table={table}
          cid="treatment-group-tables"
          updateHandler={(_, {table}: {table: Table}) => {
            updateTableCells(table, props.setTreatmentGroups, persistedTreatmentGroupsById);
          }}
          storeState={noop}
          restoreState={noop}
          isCollapsible={true}
          hideRowCount={true}
          allowReorderDimensions={{
            [TableDimension.ROW]: true,
            [TableDimension.COLUMN]: false,
          }}
          dataTestAttrs={{component: 'TreatmentGroupTable', element: 'simpleTableEditable'}}
        />
      </div>
    </div>
  );
}
